import moment from 'moment';
import relativeDateRangeToDateRange from '../../../relativeDateRangeToDateRange';
import migrateRelativeDateRange from '../../../migrateRelativeDateRange';

const translateAdvancedFrom = (
  relativeDateInput: RelativeDateRange,
  weekStartsOn: WeekStartsOn,
) => {
  const today = moment().format('ddd').toUpperCase();

  if (
    weekStartsOn === today &&
    relativeDateInput.type === 'current' &&
    relativeDateInput.interval === 'week' &&
    relativeDateInput.currentToDate
  ) {
    return {
      type: 'last' as 'last',
      n: 1,
      interval: 'week' as 'week',
      currentToDate: false,
    };
  }

  return relativeDateInput;
};

const getCurrentDateRange = ({
  relativeDateRange,
  weekStartsOn,
  advancedRelativeDateRange,
  dateRange,
  selectedBonusPeriod,
  selectedPeriod,
  isScorecardPopupReport,
  isUpcomingEventsBoard,
  todayOverride,
  allFiscals,
}: {
  relativeDateRange?: RelativeDateRange;
  weekStartsOn: WeekStartsOn;
  dateRange?: DateRangeInput;
  advancedRelativeDateRange?: AdvancedRelativeDateRange;
  selectedBonusPeriod?: BonusPeriod | BonusTypes.BonusPeriod;
  selectedPeriod?: Period;
  isScorecardPopupReport?: boolean;
  isUpcomingEventsBoard?: boolean;
  todayOverride?: string;
  allFiscals?: FixedPeriods.AllFiscals;
}) => {
  if (selectedPeriod && isUpcomingEventsBoard) {
    return {
      startDate: selectedPeriod.startDate,
      endDate: selectedPeriod.endDate,
    };
  } else if (selectedPeriod && !isScorecardPopupReport) {
    const yesterday = moment().subtract({ day: 1 }).format('YYYY-MM-DD');
    const shouldUseYesterday =
      yesterday < selectedPeriod.endDate &&
      selectedPeriod.startDate <= yesterday;
    return {
      startDate: selectedPeriod.startDate,
      endDate: shouldUseYesterday ? yesterday : selectedPeriod.endDate,
    };
  } else if (selectedBonusPeriod && !isScorecardPopupReport) {
    return {
      startDate: selectedBonusPeriod.startDate,
      endDate: selectedBonusPeriod.endDate,
    };
  } else if (dateRange) {
    return dateRange;
  } else if (relativeDateRange) {
    return relativeDateRangeToDateRange({
      relativeDateRange: migrateRelativeDateRange(relativeDateRange),
      startOfWeek: weekStartsOn,
      todayOverride,
      fiscals: allFiscals,
    });
  } else if (advancedRelativeDateRange) {
    /**
     * In an advanced date range, we build two date ranges and apply
     * rules to pluck out a start and end date from each to build a single
     * date range
     */
    const from = relativeDateRangeToDateRange({
      relativeDateRange: translateAdvancedFrom(
        advancedRelativeDateRange.from,
        weekStartsOn,
      ),
      startOfWeek: weekStartsOn,
      todayOverride,
      fiscals: allFiscals,
    });
    const to = relativeDateRangeToDateRange({
      relativeDateRange: advancedRelativeDateRange.to,
      startOfWeek: weekStartsOn,
      todayOverride,
      fiscals: allFiscals,
    });

    /**
     * See DateInput.test.tsx for information on why the config field
     * can be undefined
     */
    const startDate = (() => {
      if (!advancedRelativeDateRange.config) {
        if (advancedRelativeDateRange.from.type === 'next') {
          return from['endDate'];
        }
        return from['startDate'];
      }

      const fromConfig = advancedRelativeDateRange.config.from;
      if (fromConfig === 'start') {
        return from['startDate'];
      }

      return from['endDate'];
    })();

    const endDate = (() => {
      if (!advancedRelativeDateRange.config) {
        if (
          advancedRelativeDateRange.from.type === 'next' ||
          advancedRelativeDateRange.to.type === 'next'
        ) {
          return to['endDate'];
        }

        return to['startDate'];
      }

      const toConfig = advancedRelativeDateRange.config.to;
      if (toConfig === 'start') {
        return to['startDate'];
      }

      if (toConfig === 'end') {
        return to['endDate'];
      }

      const startOfPeriod = to['startDate'];
      return moment
        .utc(startOfPeriod)
        .subtract({ day: 1 })
        .format('YYYY-MM-DD');
    })();

    return {
      startDate,
      endDate,
    };
  } else {
    return undefined;
  }
};

export default getCurrentDateRange;
